import {Nullable} from '../../app/TypeHelpers'
import {MeetingAttachment} from './model/Meeting'
import {ROLE_FOR_ADMINISTRATOR, ROLE_FOR_RESIDENT} from '../../CodeMaster'
import MeetingComment, {CommentReactedUser, CommentUser} from './model/MeetingComment'
import DeletedComment from './model/DeletedComment'
import MeetingCommentView from './model/MeetingCommentView'
import DeletedCommentView from './model/DeletedCommentView'
import MeetingCommentReply from './model/MeetingCommentReply'
import DeletedCommentReply from './model/DeletedCommentReply'
import MeetingSimple from './model/MeetingSimple'
import MeetingAttendanceResponse from './model/MeetingAttendanceResponse'
import {Room} from './model/Room'

export interface MeetingResponse {
    id: number
    name: string
    date: string
    startTime: Nullable<string>
    endTime: Nullable<string>
    url: Nullable<string>
    place: Nullable<string>
    memo: Nullable<string>
    contents: Nullable<string>
    attendanceReplyDeadline: Nullable<string>
    attendanceReplyRequestDatetime: Nullable<string>
    manageAttendance: string
    proxyAttendanceAvailability: Nullable<string>
    meetingAnnounceNotifyDatetime: Nullable<string>
    meetingAttachmentFileList: MeetingAttachment[]
    meetingAttachmentLinkList: MeetingAttachment[]
    meetingReportList: MeetingAttachment[]
    meetingCommentList: MeetingCommentViewResponse[]
    version: number
}

export interface MeetingSimpleResponse extends Omit<MeetingSimple, 'attendanceReplyDeadline' | 'proxyAttendanceAvailability'> {
    attendanceReplyDeadline: Nullable<string>
    proxyAttendanceAvailability: Nullable<string>
}

export interface MeetingAttendanceResponseResponse extends Omit<MeetingAttendanceResponse, 'attendanceStatus' | 'responseDateTime'> {
    attendanceStatus: string
    responseDateTime: string
}

export interface CommentReactedUserResponse {
    familyName: string
    firstName: string
    roleId: string
    roomList: Room[],
    isWithinTerm: Nullable<boolean>
    hasMailAddress: Nullable<boolean>
    isYou: boolean
}

interface CommentAttachmentResponse {
    id: number
    displayName: string
    uri: string
}

export interface MeetingCommentResponse {
    meetingCommentId: number
    content: Nullable<string>
    familyName: Nullable<string>
    firstName: Nullable<string>
    roleId: Nullable<string>
    roomList: Room[]
    isWithinTerm: Nullable<boolean>
    hasMailAddress: Nullable<boolean>
    updateDatetime: Nullable<string>
    commentReactedUserList: Nullable<CommentReactedUserResponse[]>
    attachmentList: Nullable<CommentAttachmentResponse[]>
    isDeleted: boolean
    isYou: Nullable<boolean>
    version: Nullable<number>
}

export interface MeetingCommentViewResponse extends MeetingCommentResponse {
    replyCount: number
    latestReplyDatetime: Nullable<string>
}

export interface MeetingCommentDetailResponse extends MeetingCommentResponse {
    commentReplyList: MeetingCommentResponse[]
}

function convertRoleIdToRole(roleId: string): ROLE_FOR_ADMINISTRATOR | ROLE_FOR_RESIDENT {
    let role: ROLE_FOR_ADMINISTRATOR | ROLE_FOR_RESIDENT
    try {
        role = ROLE_FOR_RESIDENT.codeToEnumConstants(roleId)
    } catch (error) {
        role = ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(roleId)
    }
    return role
}

function convertToCommentUser(
    data: {
        familyName: Nullable<string>,
        firstName: Nullable<string>,
        roleId: Nullable<string>,
        roomList: Room[],
        isWithinTerm: Nullable<boolean>,
        hasMailAddress: Nullable<boolean>
    },
): CommentUser {
    const roomList: Room[] = data.roomList.map(room => {
        return Room.from(room)
    })
    return {
        familyName: data.familyName ?? '',
        firstName: data.firstName ?? '',
        role: convertRoleIdToRole(data.roleId ?? ''),
        roomList: roomList,
        isWithinTerm: data.isWithinTerm ?? false,
        hasMailAddress: data.hasMailAddress ?? false,
    }
}

export function convertToCommentReactedUser(user: CommentReactedUserResponse): CommentReactedUser {
    return {
        ...convertToCommentUser(user),
        isYou: user.isYou,
    }
}

export function convertToMeetingComment(data: MeetingCommentResponse): MeetingComment | DeletedComment {
    if (data.isDeleted) {
        return new DeletedComment(
            data.meetingCommentId,
            'このコメントは削除されました。',
        )
    }

    const commentReactedUserList: CommentReactedUser[] = data.commentReactedUserList ? data.commentReactedUserList
        .map((user) => convertToCommentReactedUser(user)) : []

    return new MeetingComment(
        data.meetingCommentId,
        data.content ?? '',
        convertToCommentUser(data),
        data.updateDatetime ?? '',
        commentReactedUserList,
        data.attachmentList ?? [],
        data.isYou ?? false,
        data.version ?? 0,
    )
}

export function convertToMeetingCommentView(data: MeetingCommentViewResponse): MeetingCommentView | DeletedCommentView {
    const converted = convertToMeetingComment(data)

    if (converted instanceof DeletedComment) {
        return new DeletedCommentView(
            converted.meetingCommentId,
            converted.message,
            data.replyCount,
            data.latestReplyDatetime,
        )
    } else {
        return new MeetingCommentView(
            converted.meetingCommentId,
            converted.content,
            converted.user,
            converted.updateDatetime,
            converted.commentReactedUserList,
            converted.attachmentList,
            converted.isYou,
            converted.version,
            data.replyCount,
            data.latestReplyDatetime,
        )
    }
}

export function convertToMeetingCommentReply(
    data: MeetingCommentResponse,
    replySourceId: number,
): MeetingCommentReply | DeletedCommentReply {
    const meetingComment = convertToMeetingComment(data)
    if (meetingComment instanceof DeletedComment) {
        return new DeletedCommentReply(
            meetingComment.meetingCommentId,
            meetingComment.message,
        )
    } else {
        return new MeetingCommentReply(
            meetingComment.meetingCommentId,
            replySourceId,
            meetingComment.content,
            meetingComment.user,
            meetingComment.updateDatetime,
            meetingComment.commentReactedUserList,
            meetingComment.attachmentList,
            meetingComment.isYou,
            meetingComment.version,
        )
    }
}
